import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, Link2, Loader2, RefreshCw, Unlink, UserX } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';

/**
 * Who is connected to Slack, and who is switched on (Task 2, admin half).
 *
 * WHY THIS SCREEN IS THE ONE THAT GATES GOING LIVE.
 * The backend has had every endpoint behind this since August — sync, manual
 * link, the rollout switch, unlink — and none of them had a way to be reached
 * except by hand-writing an HTTP call. So on the day the Slack credentials
 * arrive, an admin can connect the workspace and then do nothing at all with
 * it. This screen is the difference between "installed" and "usable".
 *
 * THE ROLLOUT SWITCH IS THE POINT.
 * Ryan asked for a staged rollout in as many words: himself first, then a few
 * people, then a few more, so he can train them before they wake up to DMs.
 * `sync` therefore creates everybody with notifications OFF, and the toggle in
 * the Linked section is how they come on, one person at a time. That is why
 * the toggle is the most prominent control here and why the empty state says
 * what it says: a linked person is not a notified person.
 *
 * SUGGESTIONS, NEVER SILENT GUESSES.
 * Ryan's instruction for people whose Slack and Jarvis addresses differ was
 * "manual admin mapping but with suggestions (i.e. the name matches but the
 * email is different)". The matcher scores candidates and hands back the
 * reason it liked each one, in plain words. This screen shows that reason next
 * to every candidate rather than a confidence number, because an admin is
 * being asked to confirm a person, not to audit an algorithm. Nothing below an
 * exact email match is ever applied without someone choosing it.
 *
 * WHAT IS DELIBERATELY NOT HERE.
 * No per-type mutes and no quiet hours. Those are the person's own settings
 * and live under Settings → Notifications, where they belong to whoever is
 * looking at them. An admin deciding what somebody else may hear about is a
 * different thing from an admin deciding whether somebody is in the rollout
 * yet, and merging the two would quietly hand admins the first power while
 * they thought they were using the second.
 */

/** Ineligible codes come from the server as stable strings; the wording is ours. */
const INELIGIBLE_REASONS: Record<string, string> = {
  no_email: 'No email address on their Jarvis account',
  inactive_account: 'Their Jarvis account is deactivated',
  email_domain_not_permitted: 'Not a Stemmler email address',
};

const MATCH_METHODS: Record<string, string> = {
  email_exact: 'Matched by email',
  manual: 'Linked by an admin',
};

interface TenantUser {
  id: number;
  name: string | null;
  email: string | null;
  is_active?: boolean;
  role?: string;
}

interface SlackMember {
  id: string;
  name: string | null;
  real_name: string | null;
  display_name: string | null;
  email: string | null;
}

interface MappedRow {
  id: number;
  user_id: number;
  slack_user_id: string;
  slack_email: string | null;
  slack_display_name: string | null;
  match_method: string;
  notifications_enabled: boolean;
  /** Whether the one-time welcome DM has actually reached them. */
  welcomed: boolean;
  user: TenantUser | null;
}

interface Candidate {
  member: SlackMember;
  reason: string;
}

interface Overview {
  connected: boolean;
  mapped: MappedRow[];
  auto_available?: Array<{ user: TenantUser; member: SlackMember; reason: string }>;
  suggested: Array<{ user: TenantUser; candidates: Candidate[] }>;
  unmatched: Array<{ user: TenantUser }>;
  ineligible: Array<{ user: TenantUser; reason: string }>;
}

/** A Slack member as a person reads them, not as Slack stores them. */
const memberLabel = (m: SlackMember) =>
  m.real_name || m.display_name || m.name || m.id;

const personLabel = (u: TenantUser | null, fallbackId: number) =>
  u?.name || u?.email || `User ${fallbackId}`;

const SlackMappingCard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [data, setData] = useState<Overview | null>(null);
  const [syncing, setSyncing] = useState(false);
  /** The user id currently being changed, so only that row's controls lock. */
  const [busy, setBusy] = useState<number | null>(null);
  /** Chosen Slack id per user in the suggestion and no-match sections. */
  const [picked, setPicked] = useState<Record<number, string>>({});

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const { data: body } = await api.get('/api/integrations/slack/mappings');
      setData(body as Overview);
      setFailed(false);
    } catch {
      // This card only renders for a tenant that may use integrations, so a
      // failure here is a fault rather than an answer about permission.
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runSync = async () => {
    setSyncing(true);
    try {
      const { data: body } = await api.post('/api/integrations/slack/mappings/sync');
      const created = body?.created?.length ?? 0;
      toast.success(
        created
          ? `Linked ${created} ${created === 1 ? 'person' : 'people'}. Notifications stay off until you switch them on.`
          : 'Nothing new to link — every exact email match is already connected.'
      );
      await load(true);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not sync the Slack members.');
    } finally {
      setSyncing(false);
    }
  };

  const setEnabled = async (userId: number, enabled: boolean) => {
    setBusy(userId);
    try {
      await api.patch(`/api/integrations/slack/mappings/${userId}`, {
        notifications_enabled: enabled,
      });
      toast.success(enabled ? 'Notifications switched on.' : 'Notifications switched off.');
      await load(true);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not change that setting.');
    } finally {
      setBusy(null);
    }
  };

  const link = async (userId: number, slackUserId: string) => {
    if (!slackUserId) return;
    setBusy(userId);
    try {
      await api.post('/api/integrations/slack/mappings', {
        user_id: userId,
        slack_user_id: slackUserId,
      });
      toast.success('Linked. Notifications stay off until you switch them on.');
      setPicked((p) => {
        const next = { ...p };
        delete next[userId];
        return next;
      });
      await load(true);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not link that person.');
    } finally {
      setBusy(null);
    }
  };

  const unlink = async (userId: number, name: string) => {
    // Unlinking throws away the record of who this person is in Slack, so it
    // asks first. Switching notifications off is the reversible thing and is
    // one click; this is not, and should not feel like it.
    if (!window.confirm(`Disconnect ${name} from Slack? Their notifications stop immediately.`)) {
      return;
    }
    setBusy(userId);
    try {
      await api.delete(`/api/integrations/slack/mappings/${userId}`);
      toast.success('Disconnected.');
      await load(true);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not disconnect that person.');
    } finally {
      setBusy(null);
    }
  };

  const header = (
    <div className="flex items-start gap-3">
      <div className="p-2 bg-gray-100 rounded-lg">
        <Link2 className="w-5 h-5 text-gray-600" />
      </div>
      <div className="flex-1">
        <h2 className="text-sm font-semibold text-gray-900">People</h2>
        <p className="mt-1 text-sm text-gray-600">
          Who is connected to Slack, and who is switched on. Linking somebody does not start
          their notifications — you switch each person on here when they are ready.
        </p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5 mt-6" data-testid="slack-mapping-card">
        {header}
        <div className="flex items-center gap-2 text-sm text-gray-500 mt-4">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading…
        </div>
      </div>
    );
  }

  if (failed || !data) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5 mt-6" data-testid="slack-mapping-card">
        {header}
        <div className="flex items-start gap-2 mt-4 p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            Could not load who is connected.
            <button
              type="button"
              onClick={() => load()}
              className="ml-2 underline font-medium hover:text-red-900"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const enabledCount = data.mapped.filter((m) => m.notifications_enabled).length;
  const autoAvailable = data.auto_available || [];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mt-6" data-testid="slack-mapping-card">
      {header}

      {!data.connected ? (
        /* getOverview answers without an install rather than erroring, so this
           is a state and not a failure. It still lists anyone already linked —
           disconnecting a workspace does not erase who was who. */
        <div
          className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600"
          data-testid="slack-mapping-not-connected"
        >
          Connect the Slack workspace above first. Once it is connected, this shows everyone in
          Jarvis and who they are in Slack.
        </div>
      ) : (
        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={runSync}
            disabled={syncing}
            data-testid="slack-mapping-sync"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {syncing ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" />
            )}
            Link everyone whose email matches
          </button>
          <span className="text-xs text-gray-500">
            {data.mapped.length} linked · {enabledCount} switched on
          </span>
        </div>
      )}

      {/* ── Linked ─────────────────────────────────────────────────────── */}
      {data.mapped.length > 0 && (
        <section className="mt-6" data-testid="slack-mapping-linked">
          <p className="text-xs font-medium text-gray-700 mb-2">Linked</p>
          <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
            {data.mapped.map((m) => {
              const name = personLabel(m.user, m.user_id);
              const rowBusy = busy === m.user_id;
              return (
                <li key={m.user_id} className="flex items-center gap-3 px-3 py-2.5">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {m.slack_display_name || m.slack_user_id}
                      {m.slack_email ? ` · ${m.slack_email}` : ''}
                      {' · '}
                      {MATCH_METHODS[m.match_method] || m.match_method}
                    </p>
                    {/* Only the unhappy case is shown. `welcomed_at` is stamped
                        after Slack accepts the message, so a switched-on person
                        without it was never actually told — and the only way to
                        retry is to switch them off and on again. Saying nothing
                        would leave an admin believing somebody had been warned
                        when they had not. */}
                    {m.notifications_enabled && !m.welcomed && (
                      <p
                        className="text-xs text-amber-700 mt-0.5"
                        data-testid={`slack-mapping-unwelcomed-${m.user_id}`}
                      >
                        Welcome message not delivered — switch them off and on to try again
                      </p>
                    )}
                  </div>

                  <button
                    type="button"
                    role="switch"
                    aria-checked={m.notifications_enabled}
                    aria-label={`Notifications for ${name}`}
                    disabled={rowBusy}
                    onClick={() => setEnabled(m.user_id, !m.notifications_enabled)}
                    data-testid={`slack-mapping-toggle-${m.user_id}`}
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors disabled:opacity-50 ${
                      m.notifications_enabled ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 mt-0.5 ml-0.5 rounded-full bg-white transition-transform ${
                        m.notifications_enabled ? 'translate-x-4' : 'translate-x-0'
                      }`}
                    />
                  </button>
                  <span className="text-xs text-gray-500 w-14 flex-shrink-0">
                    {m.notifications_enabled ? 'On' : 'Off'}
                  </span>

                  <button
                    type="button"
                    disabled={rowBusy}
                    onClick={() => unlink(m.user_id, name)}
                    aria-label={`Disconnect ${name} from Slack`}
                    data-testid={`slack-mapping-unlink-${m.user_id}`}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    <Unlink className="w-4 h-4" />
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* ── Ready to link (exact email match, no row yet) ──────────────── */}
      {autoAvailable.length > 0 && (
        <section className="mt-6" data-testid="slack-mapping-ready">
          <p className="text-xs font-medium text-gray-700 mb-2">
            Ready to link — their email matches exactly
          </p>
          <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
            {autoAvailable.map(({ user, member }) => (
              <li key={user.id} className="flex items-center gap-3 px-3 py-2.5">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {personLabel(user, user.id)}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {memberLabel(member)}
                    {member.email ? ` · ${member.email}` : ''}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={busy === user.id}
                  onClick={() => link(user.id, member.id)}
                  data-testid={`slack-mapping-link-${user.id}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50"
                >
                  <Check className="w-3.5 h-3.5" />
                  Link
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ── Needs a decision ───────────────────────────────────────────── */}
      {data.suggested.length > 0 && (
        <section className="mt-6" data-testid="slack-mapping-suggested">
          <p className="text-xs font-medium text-gray-700 mb-1">Needs your decision</p>
          <p className="text-xs text-gray-500 mb-2">
            Their Jarvis and Slack addresses are different. Pick the right person — nothing is
            linked until you do.
          </p>
          <ul className="space-y-2">
            {data.suggested.map(({ user, candidates }) => {
              const chosen = picked[user.id] ?? candidates[0]?.member.id ?? '';
              const chosenReason = candidates.find((c) => c.member.id === chosen)?.reason;
              return (
                <li key={user.id} className="border border-gray-200 rounded-lg px-3 py-2.5">
                  <p className="text-sm font-medium text-gray-900">{personLabel(user, user.id)}</p>
                  <p className="text-xs text-gray-500">{user.email}</p>
                  <div className="mt-2 flex items-center gap-2">
                    <select
                      value={chosen}
                      disabled={busy === user.id}
                      onChange={(e) => setPicked((p) => ({ ...p, [user.id]: e.target.value }))}
                      aria-label={`Slack account for ${personLabel(user, user.id)}`}
                      data-testid={`slack-mapping-pick-${user.id}`}
                      className="flex-1 min-w-0 text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white disabled:opacity-50"
                    >
                      {candidates.map((c) => (
                        <option key={c.member.id} value={c.member.id}>
                          {memberLabel(c.member)}
                          {c.member.email ? ` — ${c.member.email}` : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      disabled={busy === user.id || !chosen}
                      onClick={() => link(user.id, chosen)}
                      data-testid={`slack-mapping-confirm-${user.id}`}
                      className="px-2.5 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex-shrink-0"
                    >
                      Link
                    </button>
                  </div>
                  {/* The matcher's own words for why this candidate came up. An
                      admin is confirming a person, so the reason matters more
                      than the score behind it. */}
                  {chosenReason && <p className="mt-1.5 text-xs text-gray-500">{chosenReason}</p>}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* ── No match at all ────────────────────────────────────────────── */}
      {data.unmatched.length > 0 && (
        <section className="mt-6" data-testid="slack-mapping-unmatched">
          <p className="text-xs font-medium text-gray-700 mb-1">No Slack account found</p>
          <p className="text-xs text-gray-500 mb-2">
            Nothing in the workspace resembles them. If they do have an account, open their Slack
            profile → More → Copy member ID, and paste it here.
          </p>
          <ul className="space-y-2">
            {data.unmatched.map(({ user }) => (
              <li
                key={user.id}
                className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2.5"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {personLabel(user, user.id)}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{user.email}</p>
                </div>
                <input
                  type="text"
                  value={picked[user.id] || ''}
                  disabled={busy === user.id}
                  onChange={(e) => setPicked((p) => ({ ...p, [user.id]: e.target.value.trim() }))}
                  placeholder="Slack member ID"
                  aria-label={`Slack member ID for ${personLabel(user, user.id)}`}
                  data-testid={`slack-mapping-manual-${user.id}`}
                  className="w-40 text-sm border border-gray-300 rounded-lg px-2 py-1.5 disabled:opacity-50"
                />
                <button
                  type="button"
                  disabled={busy === user.id || !picked[user.id]}
                  onClick={() => link(user.id, picked[user.id])}
                  data-testid={`slack-mapping-manual-link-${user.id}`}
                  className="px-2.5 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex-shrink-0"
                >
                  Link
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ── Cannot be linked ───────────────────────────────────────────── */}
      {data.ineligible.length > 0 && (
        <section className="mt-6" data-testid="slack-mapping-ineligible">
          <p className="text-xs font-medium text-gray-700 mb-1">Cannot be linked</p>
          <p className="text-xs text-gray-500 mb-2">
            These people are deliberately left out. No record is kept for them and nothing is sent
            about them.
          </p>
          <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
            {data.ineligible.map(({ user, reason }) => (
              <li key={user.id} className="flex items-center gap-3 px-3 py-2">
                <UserX className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-700 truncate">{personLabel(user, user.id)}</p>
                  <p className="text-xs text-gray-500 truncate">{user.email}</p>
                </div>
                <span className="text-xs text-gray-500 flex-shrink-0">
                  {INELIGIBLE_REASONS[reason] || reason}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {data.connected &&
        data.mapped.length === 0 &&
        autoAvailable.length === 0 &&
        data.suggested.length === 0 &&
        data.unmatched.length === 0 && (
          <p className="mt-6 text-sm text-gray-500" data-testid="slack-mapping-empty">
            Nobody in Jarvis can be linked to this workspace yet.
          </p>
        )}
    </div>
  );
};

export default SlackMappingCard;
